import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent
} from 'react';
import type { CanvasFormat, DrawingReplay, DrawingTool, ReplayPoint, ReplayStroke } from '../types';

type Stroke = ReplayStroke;

export interface DrawingCanvasHandle {
  exportImage: () => string;
  exportSubmission: () => { image: string; replay: DrawingReplay };
}

interface DrawingCanvasProps {
  canvasFormat?: CanvasFormat;
  blindAfterMs?: number;
  blindLabel?: string;
  initialImage?: string;
  initialReplay?: DrawingReplay;
  onDrawingChange?: () => void;
}

const COLORS = [
  '#17182a', '#ffffff', '#164e3b', '#22c55e', '#a16207', '#facc15', '#4b5563', '#cbd5e1',
  '#7f1d1d', '#ef4444', '#701a75', '#ec4899', '#1d4ed8', '#22d3ee', '#78350f', '#f97316', '#fb7185', '#fed7aa'
];

const TOOLS: Array<{ id: DrawingTool; label: string; shortcut: string }> = [
  { id: 'pencil', label: 'قلم', shortcut: 'B' },
  { id: 'eraser', label: 'ممحاة', shortcut: 'E' },
  { id: 'line', label: 'خط', shortcut: 'L' },
  { id: 'rect', label: 'مستطيل', shortcut: 'R' },
  { id: 'ellipse', label: 'دائرة', shortcut: 'O' },
  { id: 'rect-fill', label: 'مستطيل ممتلئ', shortcut: 'Shift+R' },
  { id: 'ellipse-fill', label: 'دائرة ممتلئة', shortcut: 'Shift+O' },
  { id: 'fill', label: 'تعبئة', shortcut: 'F' }
];

const DrawingCanvas = forwardRef<DrawingCanvasHandle, DrawingCanvasProps>(function DrawingCanvas({ canvasFormat = 'standard', blindAfterMs = 0, blindLabel = 'أنت ترسم أعمى الآن', initialImage, initialReplay, onDrawingChange }, ref) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const redoRef = useRef<Stroke[]>([]);
  const drawingRef = useRef(false);
  const activeStrokeRef = useRef<Stroke | null>(null);
  const baseImageRef = useRef<HTMLImageElement | null>(null);
  const initialImageAppliedRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const blindTimerRef = useRef<number | null>(null);
  const blindTickRef = useRef<number | null>(null);
  const blindDeadlineRef = useRef<number | null>(null);
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(8);
  const [opacity, setOpacity] = useState(1);
  const [tool, setTool] = useState<DrawingTool>('pencil');
  const [grid, setGrid] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [blindArmed, setBlindArmed] = useState(false);
  const [blindActive, setBlindActive] = useState(false);
  const [blindRemainingMs, setBlindRemainingMs] = useState(0);
  const [, bump] = useState(0);

  const renderAction = useCallback((ctx: CanvasRenderingContext2D, stroke: Stroke, pointsLimit?: number) => {
    const toolId = stroke.tool ?? (stroke.eraser ? 'eraser' : 'pencil');
    const points = typeof pointsLimit === 'number' ? stroke.points.slice(0, pointsLimit) : stroke.points;
    if (!points.length) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / Math.max(1, rect.width);
    const sy = canvas.height / Math.max(1, rect.height);
    const scale = (sx + sy) / 2;
    const start = points[0];
    const end = points.at(-1) ?? start;

    ctx.save();
    ctx.globalAlpha = stroke.opacity ?? 1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = toolId === 'eraser' ? '#ffffff' : stroke.color;
    ctx.fillStyle = toolId === 'eraser' ? '#ffffff' : stroke.color;
    ctx.lineWidth = stroke.size * scale;

    if (toolId === 'fill') {
      ctx.restore();
      floodFill(ctx, canvas, start.x, start.y, stroke.color, stroke.opacity ?? 1);
      return;
    }

    if (toolId === 'line') {
      ctx.beginPath();
      ctx.moveTo(start.x * canvas.width, start.y * canvas.height);
      ctx.lineTo(end.x * canvas.width, end.y * canvas.height);
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (toolId === 'rect' || toolId === 'rect-fill') {
      const x = Math.min(start.x, end.x) * canvas.width;
      const y = Math.min(start.y, end.y) * canvas.height;
      const w = Math.abs(end.x - start.x) * canvas.width;
      const h = Math.abs(end.y - start.y) * canvas.height;
      toolId === 'rect-fill' ? ctx.fillRect(x, y, w, h) : ctx.strokeRect(x, y, w, h);
      ctx.restore();
      return;
    }

    if (toolId === 'ellipse' || toolId === 'ellipse-fill') {
      const cx = ((start.x + end.x) / 2) * canvas.width;
      const cy = ((start.y + end.y) / 2) * canvas.height;
      const rx = Math.abs(end.x - start.x) * canvas.width / 2;
      const ry = Math.abs(end.y - start.y) * canvas.height / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, Math.max(0.5, rx), Math.max(0.5, ry), 0, 0, Math.PI * 2);
      toolId === 'ellipse-fill' ? ctx.fill() : ctx.stroke();
      ctx.restore();
      return;
    }

    ctx.beginPath();
    ctx.moveTo(start.x * canvas.width, start.y * canvas.height);
    if (points.length === 1) {
      ctx.arc(start.x * canvas.width, start.y * canvas.height, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x * canvas.width, points[i].y * canvas.height);
      ctx.stroke();
    }
    ctx.restore();
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    if (baseImageRef.current) {
      try { ctx.drawImage(baseImageRef.current, 0, 0, canvas.width, canvas.height); } catch { /* keep white background */ }
    }
    for (const stroke of strokesRef.current) renderAction(ctx, stroke);
    if (activeStrokeRef.current) renderAction(ctx, activeStrokeRef.current);
  }, [renderAction]);

  const fitCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const width = Math.max(1, Math.round(rect.width * ratio));
    const height = Math.max(1, Math.round(rect.height * ratio));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      redraw();
    }
  }, [redraw]);

  useEffect(() => {
    // Apply a restored draft only once when this canvas instance mounts. If the
    // socket reconnects while the page stayed open, the in-memory strokes are
    // already newer than the server snapshot and must not be painted twice.
    if (initialImageAppliedRef.current) return;
    initialImageAppliedRef.current = true;
    if (initialReplay?.strokes?.length) {
      baseImageRef.current = null;
      strokesRef.current = initialReplay.strokes.map((stroke) => ({
        ...stroke,
        points: stroke.points.map((point) => ({ ...point }))
      }));
      redoRef.current = [];
      startedAtRef.current = performance.now() - Math.max(0, initialReplay.durationMs || 0);
      redraw();
      bump((value) => value + 1);
      return;
    }
    if (!initialImage) {
      baseImageRef.current = null;
      strokesRef.current = [];
      redoRef.current = [];
      redraw();
      return;
    }
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      baseImageRef.current = image;
      redraw();
    };
    image.onerror = () => {
      if (cancelled) return;
      baseImageRef.current = null;
      redraw();
    };
    image.src = initialImage;
    return () => { cancelled = true; };
  }, [initialImage, initialReplay, redraw]);

  useEffect(() => {
    fitCanvas();
    const observer = new ResizeObserver(fitCanvas);
    if (canvasRef.current) observer.observe(canvasRef.current);
    window.addEventListener('resize', fitCanvas);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fitCanvas);
    };
  }, [fitCanvas, canvasFormat, zoom]);

  useEffect(() => () => {
    if (blindTimerRef.current !== null) window.clearTimeout(blindTimerRef.current);
    if (blindTickRef.current !== null) window.clearInterval(blindTickRef.current);
  }, []);

  const armBlindMode = useCallback(() => {
    if (!blindAfterMs || blindAfterMs <= 0 || blindArmed || blindActive) return;
    const duration = Math.max(250, blindAfterMs);
    blindDeadlineRef.current = performance.now() + duration;
    setBlindArmed(true);
    setBlindRemainingMs(duration);
    blindTickRef.current = window.setInterval(() => {
      const remaining = Math.max(0, Math.round((blindDeadlineRef.current ?? performance.now()) - performance.now()));
      setBlindRemainingMs(remaining);
      if (remaining <= 0 && blindTickRef.current !== null) {
        window.clearInterval(blindTickRef.current);
        blindTickRef.current = null;
      }
    }, 50);
    blindTimerRef.current = window.setTimeout(() => {
      setBlindActive(true);
      setBlindRemainingMs(0);
      if (blindTickRef.current !== null) window.clearInterval(blindTickRef.current);
      blindTickRef.current = null;
      blindTimerRef.current = null;
    }, duration);
  }, [blindAfterMs, blindArmed, blindActive]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement | null)?.matches('input, textarea')) return;
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && key === 'z') {
        event.preventDefault();
        event.shiftKey ? redo() : undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && key === 'y') {
        event.preventDefault();
        redo();
        return;
      }
      if (key === 'b') setTool('pencil');
      if (key === 'e') setTool('eraser');
      if (key === 'l') setTool('line');
      if (key === 'r') setTool(event.shiftKey ? 'rect-fill' : 'rect');
      if (key === 'o') setTool(event.shiftKey ? 'ellipse-fill' : 'ellipse');
      if (key === 'f') setTool('fill');
      if (key === 'g') setGrid((value) => !value);
      if (event.key === '+') setZoom((value) => Math.min(2, Number((value + 0.25).toFixed(2))));
      if (event.key === '-') setZoom((value) => Math.max(1, Number((value - 0.25).toFixed(2))));
      if (event.key === '[') setSize((value) => Math.max(3, value - 2));
      if (event.key === ']') setSize((value) => Math.min(40, value + 2));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useImperativeHandle(ref, () => ({
    exportImage: () => {
      const canvas = canvasRef.current;
      if (!canvas) throw new Error('لوحة الرسم غير جاهزة.');
      return canvas.toDataURL('image/webp', 0.88);
    },
    exportSubmission: () => {
      const canvas = canvasRef.current;
      if (!canvas) throw new Error('لوحة الرسم غير جاهزة.');
      // Include the stroke that is still under the finger/mouse. This is
      // essential at the deadline: if the player is actively drawing when
      // time expires, the server must receive the visible partial stroke too.
      const source = activeStrokeRef.current
        ? [...strokesRef.current, activeStrokeRef.current]
        : strokesRef.current;
      const strokes = source.map((stroke) => ({ ...stroke, points: stroke.points.map((point) => ({ ...point })) }));
      const lastPoint = strokes.flatMap((stroke) => stroke.points).sort((a, b) => a.t - b.t).at(-1);
      return {
        image: canvas.toDataURL('image/webp', 0.88),
        replay: { durationMs: lastPoint?.t ?? 0, strokes }
      };
    }
  }));

  const pointFromEvent = (event: ReactPointerEvent<HTMLCanvasElement>): ReplayPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (startedAtRef.current === null) startedAtRef.current = performance.now();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
      t: Math.max(0, Math.round(performance.now() - startedAtRef.current))
    };
  };

  const makeStroke = (first: ReplayPoint): Stroke => ({
    points: [first], color, size, eraser: tool === 'eraser', tool, opacity
  });

  const pointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    armBlindMode();
    const first = pointFromEvent(event);
    redoRef.current = [];
    if (tool === 'fill') {
      strokesRef.current.push(makeStroke(first));
      redraw();
      bump((value) => value + 1);
      onDrawingChange?.();
      return;
    }
    drawingRef.current = true;
    activeStrokeRef.current = makeStroke(first);
    redraw();
  };

  const pointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || !activeStrokeRef.current) return;
    const next = pointFromEvent(event);
    if (['line', 'rect', 'ellipse', 'rect-fill', 'ellipse-fill'].includes(activeStrokeRef.current.tool ?? 'pencil')) {
      activeStrokeRef.current.points = [activeStrokeRef.current.points[0], next];
      redraw();
      return;
    }
    if (activeStrokeRef.current.points.length >= 900) return;
    const previous = activeStrokeRef.current.points.at(-1)!;
    const distance = Math.hypot(next.x - previous.x, next.y - previous.y);
    if (distance < 0.0018 && next.t - previous.t < 18) return;
    activeStrokeRef.current.points.push(next);
    redraw();
  };

  const pointerUp = (event?: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || !activeStrokeRef.current) return;
    if (event && activeStrokeRef.current.points.length === 1 && activeStrokeRef.current.tool !== 'pencil' && activeStrokeRef.current.tool !== 'eraser') {
      activeStrokeRef.current.points.push(pointFromEvent(event));
    }
    strokesRef.current.push(activeStrokeRef.current);
    activeStrokeRef.current = null;
    drawingRef.current = false;
    redraw();
    bump((value) => value + 1);
    onDrawingChange?.();
  };

  const undo = () => {
    const stroke = strokesRef.current.pop();
    if (stroke) redoRef.current.push(stroke);
    redraw();
    bump((value) => value + 1);
    if (stroke) onDrawingChange?.();
  };

  const redo = () => {
    const stroke = redoRef.current.pop();
    if (stroke) strokesRef.current.push(stroke);
    redraw();
    bump((value) => value + 1);
    if (stroke) onDrawingChange?.();
  };

  const clear = () => {
    if (!strokesRef.current.length && !baseImageRef.current) return;
    // Restored drafts normally use initialReplay, so every previous stroke stays
    // undoable. If an old/fallback draft only has a raster image, clearing must
    // still remove that base image instead of leaving an undeletable drawing.
    redoRef.current = [...strokesRef.current];
    strokesRef.current = [];
    baseImageRef.current = null;
    startedAtRef.current = null;
    redraw();
    bump((value) => value + 1);
    onDrawingChange?.();
  };

  return (
    <div className={`drawing-shell canvas-${canvasFormat} ${blindAfterMs > 0 ? 'blind-enabled' : ''} ${blindActive ? 'blind-active' : ''}`}>
      <div className="drawing-toolbar pro-toolbar">
        <div className="tool-group color-row" aria-label="ألوان الرسم">
          {COLORS.map((item) => (
            <button key={item} type="button" className={`color-dot ${color === item ? 'active' : ''}`} style={{ backgroundColor: item }} aria-label={`اختيار اللون ${item}`} onClick={() => setColor(item)} />
          ))}
          <label className="custom-color" title="لون مخصص">
            <input type="color" value={color} onChange={(event) => setColor(event.target.value)} />
            <span>+</span>
          </label>
        </div>

        <div className="drawing-tools-grid">
          {TOOLS.map((item) => (
            <button key={item.id} type="button" className={`tool-btn ${tool === item.id ? 'active' : ''}`} onClick={() => setTool(item.id)} title={`${item.label} (${item.shortcut})`}>
              {item.label}
            </button>
          ))}
        </div>

        <div className="tool-group compact-tools drawing-fine-controls">
          <label className="range-label"><span>الحجم {size}</span><input type="range" min="3" max="40" value={size} onChange={(e) => setSize(Number(e.target.value))} /></label>
          <label className="range-label"><span>الشفافية {Math.round(opacity * 100)}%</span><input type="range" min="10" max="100" value={Math.round(opacity * 100)} onChange={(e) => setOpacity(Number(e.target.value) / 100)} /></label>
          <button type="button" className={`tool-btn ${grid ? 'active' : ''}`} onClick={() => setGrid((value) => !value)} title="الشبكة (G)">شبكة</button>
          <button type="button" className="tool-btn" onClick={() => setZoom((value) => Math.max(1, value - .25))} disabled={zoom <= 1} title="تصغير (-)">−</button>
          <span className="zoom-label">{Math.round(zoom * 100)}%</span>
          <button type="button" className="tool-btn" onClick={() => setZoom((value) => Math.min(2, value + .25))} disabled={zoom >= 2} title="تكبير (+)">+</button>
          <button type="button" className="tool-btn" onClick={undo} disabled={!strokesRef.current.length} title="تراجع (Ctrl+Z)">تراجع</button>
          <button type="button" className="tool-btn" onClick={redo} disabled={!redoRef.current.length} title="إعادة (Ctrl+Y)">إعادة</button>
          <button type="button" className="tool-btn danger-ghost" onClick={clear}>مسح</button>
        </div>
      </div>
      <div className="canvas-scroll">
        <div className={`canvas-stage ${grid ? 'show-grid' : ''}`} style={{ width: `${zoom * 100}%` }}>
          <div className={`canvas-wrap ${blindActive ? 'blind-mask-on' : ''} ${blindArmed && !blindActive ? 'blind-counting' : ''}`}>
            <canvas ref={canvasRef} className="drawing-canvas" onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={pointerUp} onContextMenu={(e) => e.preventDefault()} />
            {blindAfterMs > 0 && (blindArmed || blindActive) && (
              <div className={`blind-canvas-overlay ${blindActive ? 'active' : 'countdown'}`} aria-hidden="true">
                {blindActive ? (
                  <><span className="blind-eye">🙈</span><strong>{blindLabel}</strong><small>الخطوط ما زالت تُسجل… لكنك لن تراها حتى لحظة الكشف.</small><i className="blind-cursor-hint">حرّك يدك وثق بذاكرتك ✍️</i></>
                ) : (
                  <><span className="blind-eye">👁️</span><strong>احفظ أماكن خطوطك!</strong><small>ستختفي بعد {(blindRemainingMs / 1000).toFixed(1)} ثانية</small></>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

function hexToRgba(hex: string, alpha: number) {
  const value = hex.replace('#', '');
  const number = Number.parseInt(value, 16);
  return {
    r: (number >> 16) & 255,
    g: (number >> 8) & 255,
    b: number & 255,
    a: Math.max(0, Math.min(1, alpha))
  };
}

function floodFill(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, nx: number, ny: number, color: string, opacity: number) {
  const width = canvas.width;
  const height = canvas.height;
  const x = Math.max(0, Math.min(width - 1, Math.round(nx * (width - 1))));
  const y = Math.max(0, Math.min(height - 1, Math.round(ny * (height - 1))));
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  const startIndex = (y * width + x) * 4;
  const target = [data[startIndex], data[startIndex + 1], data[startIndex + 2], data[startIndex + 3]];
  const fill = hexToRgba(color, opacity);
  const newColor = [fill.r, fill.g, fill.b, Math.round(fill.a * 255)];
  if (target.every((value, index) => value === newColor[index])) return;

  const match = (index: number) => Math.abs(data[index] - target[0]) < 8 && Math.abs(data[index + 1] - target[1]) < 8 && Math.abs(data[index + 2] - target[2]) < 8 && Math.abs(data[index + 3] - target[3]) < 8;
  const stack: number[] = [y * width + x];
  const visited = new Uint8Array(width * height);
  let processed = 0;
  const maxPixels = Math.min(width * height, 2_500_000);

  while (stack.length && processed < maxPixels) {
    const pos = stack.pop()!;
    if (visited[pos]) continue;
    visited[pos] = 1;
    const px = pos % width;
    const py = Math.floor(pos / width);
    const index = pos * 4;
    if (!match(index)) continue;

    const a = fill.a;
    data[index] = Math.round(fill.r * a + data[index] * (1 - a));
    data[index + 1] = Math.round(fill.g * a + data[index + 1] * (1 - a));
    data[index + 2] = Math.round(fill.b * a + data[index + 2] * (1 - a));
    data[index + 3] = 255;
    processed++;

    if (px > 0) stack.push(pos - 1);
    if (px + 1 < width) stack.push(pos + 1);
    if (py > 0) stack.push(pos - width);
    if (py + 1 < height) stack.push(pos + width);
  }
  ctx.putImageData(image, 0, 0);
}

export default DrawingCanvas;
